/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import TQTaskScheduler from "../controller/TQTaskScheduler";
import TQFont from "./TQTypes";
import { HashAlgo, TQUtils } from "../utils/TQUtils";
import { TQFontParser } from "../utils/fontparser/TQFontParser";

export enum ByofFileError {
    NONE = "validation-passed",
    INVALID_EXTENSION = "validation-invalid-extension", // File extension is an issue, not allowed
    INVALID_SIZE = "validation-invalid-size", // File size is an issue
    INVALID_FILE_CONTENTS = "validation-file-not-a-font", // Deep checks failed of the file, File is not a valid font at all!
    INVALID_DUPLICATE = "validation-duplicate-byof", // File is a duplicate as it's already present in the cached byof fonts
    INVALID_BLOCKLISTED_AF = "validation-postscriptname-blocklisted", // Font postscript name is blocklisted by AF hence blocked
}

const MAX_FILE_UPLOAD_SIZE_MB = 512;
const VALID_FILE_EXTENSIONS = ["otf", "ttf"];

export class TQByofFileValidatorResult {
    /**
     * Check if there was an error and what was the error.
     */
    private _byofError: ByofFileError = ByofFileError.NONE;

    /**
     * Check if there was an error and whether fatal.
     */
    private _isFatalError = false;

    /**
     * File object denoting the prevalidated file
     */
    private _file: File;

    /**
     * TQFont object created from the validated BYOF Font if applicable.
     * Use this to figure out font name, script etc.
     */
    private _font: TQFont = new TQFont();

    /**
     * To know/ set the computed checksum of the file
     */
    private _fileChecksum = "";

    /** Proc to be invoked for checking if name is allowed as per Adobe Fonts policy */
    private _fontNameSanitizerProc: any = undefined;

    constructor(file: File, nameCheckerProc: any = undefined) {
        this._file = file;
        if (nameCheckerProc) {
            this._fontNameSanitizerProc = nameCheckerProc;
        }
    }

    private async _parseFontFile() {
        try {
            const fontParser = new TQFontParser(new Uint8Array(await (this._file as Blob).arrayBuffer()));
            if (fontParser.font) {
                this._font = fontParser.getTQFont();
                if (this._font.postscriptName != "") {
                    this._font.script = fontParser.computeFontScript(this._font.postscriptName);
                    this._font.addSupportedLanguage(this._font.script);
                }
            }
            if (this._font.postscriptName == "") {
                // this._font.postscriptName = this._font.fontName.trim().replace(/\s/g,'');
                this._font.fontName = this._file.name.split(".")[0].replace(/\W/g, " ").substring(0, 32);
                if (this._font.familyName == "") {
                    this._font.familyName = this._font.fontName;
                }
            }
        } catch (e) {
            // ..
        }
    }
    /**
     * Validates if the uploaded file size is bellow MAX_FILE_UPLOAD_SIZE_MB.
     *
     * @param {File} file
     * @returns {Boolean}
     */
    private _isBelowUploadSizeLimit(): boolean {
        const sizeInMb = this._file.size / 1024 / 1024;
        return sizeInMb <= MAX_FILE_UPLOAD_SIZE_MB;
    }

    /**
     * Validates if the uploaded file has the right extension.
     *
     * @param {File} file
     * @returns {Boolean}
     */
    private _hasValidExtension(): boolean {
        const extension = this._file.name.split(".").pop()?.toLocaleLowerCase();
        return !!extension && VALID_FILE_EXTENSIONS.includes(extension);
    }

    /**
     * Checks if font file is already present as an available BYOF font
     * @param file
     * @returns {Boolean} depicting if byof is present as a cached font and thus a duplicate
     */
    private async _checkByofDuplicacy(sha256sum: string) {
        return TQTaskScheduler.checkByofDuplicacy(sha256sum);
    }

    public get fileChecksum(): string {
        return this._fileChecksum;
    }
    public set fileChecksum(value: string) {
        this._fileChecksum = value;
    }

    public get byofError(): ByofFileError {
        return this._byofError;
    }
    public set byofError(value: ByofFileError) {
        this._byofError = value;
    }

    public get isFatalError() {
        return this._isFatalError;
    }
    public set isFatalError(value) {
        this._isFatalError = value;
    }

    public get font(): TQFont {
        return this._font;
    }
    public set font(value: TQFont) {
        this._font = value;
    }

    /** Important! Must be only called by TQWeb to do the actual validation and process the supplied font after creating constructor */
    public async _validateInternal(shallowRead: boolean) {
        if (this.isFatalError) {
            return;
        }

        const sha256sum = await TQUtils.getHash(HashAlgo.SHA256, new Blob([this._file]));
        this.fileChecksum = sha256sum;

        try {
            if (!this._hasValidExtension()) {
                this.byofError = ByofFileError.INVALID_EXTENSION;
                this.isFatalError = true;
            } else if (!this._isBelowUploadSizeLimit()) {
                this.byofError = ByofFileError.INVALID_SIZE;
                this.isFatalError = true;
            } else if (false == (await TQUtils.checkIfValidFontStream(this._file))) {
                this.byofError = ByofFileError.INVALID_FILE_CONTENTS;
                this.isFatalError = true;
            } else if (true == (await this._checkByofDuplicacy(sha256sum))) {
                this.byofError = ByofFileError.INVALID_DUPLICATE;
                this.isFatalError = true;
            }

            if (false == shallowRead) {
                await this._parseFontFile();
            }

            if (false == this.isFatalError && shallowRead == false) {
                if (this._font.postscriptName == "") {
                    this.byofError = ByofFileError.INVALID_FILE_CONTENTS;
                    this.isFatalError = true;
                } else if (this._font.postscriptName != "" && this._fontNameSanitizerProc) {
                    // In case psName is non empty and sanitizer proc is present
                    const tqFont: TQFont = await this._fontNameSanitizerProc(this._font.postscriptName);
                    if (tqFont.fontId != "") {
                        this.byofError = ByofFileError.INVALID_BLOCKLISTED_AF;
                        this.isFatalError = true;
                    }
                }
            }
        } catch (e) {
            // ...
            this.byofError = ByofFileError.INVALID_FILE_CONTENTS;
            this.isFatalError = true;
        }
        return false == this.isFatalError;
    }
}
